/**
 * Plain-JSON utilities shared by every engine layer.
 *
 * `GameState` is plain JSON (docs/design/03 §3): serialisable with `JSON.stringify`, cloneable, hashable.
 * These helpers clone, freeze and canonically serialise such values without touching any global other
 * than `JSON` (ADR-0001).
 */
import { EngineError } from './errors';

export type JsonPrimitive = null | boolean | number | string;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
/** A plain JSON object. */
export interface JsonObject {
  [key: string]: JsonValue;
}

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

/**
 * Deep-clones a plain JSON value (arrays and plain objects; `undefined` properties are dropped as
 * `JSON.stringify` would). Throws `EngineError('notJson')` on anything else.
 */
export function deepCloneJson<T>(value: T): T {
  return cloneValue(value, 0) as T;
}

const MAX_DEPTH = 256;

function cloneValue(value: unknown, depth: number): unknown {
  if (value === null) return null;
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return value;
    case 'number':
      if (!Number.isFinite(value)) throw new EngineError('notJson', `Non-finite number ${String(value)}`);
      return value;
    case 'object': {
      if (depth > MAX_DEPTH) throw new EngineError('notJson', 'Value is too deep (cycle?)');
      if (Array.isArray(value)) return value.map((v: unknown) => cloneValue(v, depth + 1));
      if (!isPlainObject(value)) throw new EngineError('notJson', 'Only plain objects are JSON');
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) {
        if (v === undefined) continue;
        out[k] = cloneValue(v, depth + 1);
      }
      return out;
    }
    default:
      throw new EngineError('notJson', `A ${typeof value} is not JSON`);
  }
}

/** Recursively freezes a plain JSON value in place and returns it (tests and dev builds catch mutation). */
export function deepFreeze<T>(value: T): T {
  if (typeof value === 'object' && value !== null && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const v of Object.values(value)) deepFreeze(v);
  }
  return value;
}

/**
 * Canonical JSON: object keys sorted by UTF-16 code units, no whitespace, `undefined` properties dropped.
 * Two structurally equal values always produce the same string, whatever their key insertion order, so
 * the result can be hashed (state hashes, deck hashes, replay checkpoints).
 */
export function canonicalJson(value: unknown): string {
  const parts: string[] = [];
  writeCanonical(value, parts, 0);
  return parts.join('');
}

function writeCanonical(value: unknown, out: string[], depth: number): void {
  if (value === null) {
    out.push('null');
    return;
  }
  switch (typeof value) {
    case 'string':
      out.push(JSON.stringify(value));
      return;
    case 'boolean':
      out.push(value ? 'true' : 'false');
      return;
    case 'number':
      if (!Number.isFinite(value)) throw new EngineError('notJson', `Non-finite number ${String(value)}`);
      // -0 and 0 serialise identically ("0"), as JSON.stringify does.
      out.push(JSON.stringify(value));
      return;
    case 'object': {
      if (depth > MAX_DEPTH) throw new EngineError('notJson', 'Value is too deep (cycle?)');
      if (Array.isArray(value)) {
        out.push('[');
        value.forEach((v: unknown, i) => {
          if (i > 0) out.push(',');
          if (v === undefined) throw new EngineError('notJson', 'undefined inside an array');
          writeCanonical(v, out, depth + 1);
        });
        out.push(']');
        return;
      }
      if (!isPlainObject(value)) throw new EngineError('notJson', 'Only plain objects are JSON');
      const record = value as Record<string, unknown>;
      const keys = Object.keys(record)
        .filter((k) => record[k] !== undefined)
        .sort();
      out.push('{');
      keys.forEach((k, i) => {
        if (i > 0) out.push(',');
        out.push(JSON.stringify(k), ':');
        writeCanonical(record[k], out, depth + 1);
      });
      out.push('}');
      return;
    }
    default:
      throw new EngineError('notJson', `A ${typeof value} is not JSON`);
  }
}

/** Structural equality of two plain JSON values (key order ignored). */
export function jsonEqual(a: unknown, b: unknown): boolean {
  return canonicalJson(a) === canonicalJson(b);
}
